import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment.development';
import { SnackbarService } from '../../core/services/snackbar.service';
import { HugeiconsIconComponent } from '@hugeicons/angular';
import { CheckmarkCircle02Icon } from '@hugeicons/core-free-icons';
import { DynamicHeightDirective } from '../../shared/directives/dynamic-height.directive';

interface NotificationSetting {
    id: number;
    notification_type: string;
    admin: number;
    patient: number;
    attorney: number;
}

@Component({
    selector: 'app-notification-settings',
    standalone: true,
    imports: [CommonModule, FormsModule, HugeiconsIconComponent, DynamicHeightDirective],
    templateUrl: './notification-settings.component.html',
    styleUrl: './notification-settings.component.scss'
})
export class NotificationSettingsComponent implements OnInit {
    settings: NotificationSetting[] = [];
    loading = true;
    saving = false;

    checkIcon = CheckmarkCircle02Icon;

    private apiUrl = `${environment.apiUrl}/api/notification-settings`;

    /** Fallback labels when API doesn't return notification_type */
    private readonly defaultLabels: Record<number, string> = {
        1: 'New Case Inquiry Submitted',
        2: 'Case Inquiry Accepted',
        3: 'Case Inquiry Rejected',
        4: 'Case Details Submitted',
        5: 'Offer Generated',
        6: 'Offer Accepted',
        7: 'Offer Rejected',
        8: 'Documents Signed & Uploaded',
        9: 'Documents Approved',
        10: 'Documents Rejected',
        11: 'Payment Details Submitted',
        12: 'Payment Sent',
        13: 'Release of Lien Uploaded',
        14: 'Case Closed',
        15: 'Hospital Payment Submitted',
    };

    constructor(
        private http: HttpClient,
        private snackbar: SnackbarService
    ) {}

    ngOnInit(): void {
        this.loadSettings();
    }

    loadSettings(): void {
        this.loading = true;
        this.http.get<any>(this.apiUrl, {
            headers: new HttpHeaders({ 'X-Skip-Loader': 'true' })
        }).subscribe({
            next: (response) => {
                this.settings = (response?.data || response?.settings || response || []).map((s: any) => ({
                    id: s.id,
                    notification_type: s.notification_type || s.type || this.defaultLabels[s.id] || `Notification #${s.id}`,
                    admin: s.admin ? 1 : 0,
                    patient: s.patient ? 1 : 0,
                    attorney: s.attorney ? 1 : 0,
                }));
                this.loading = false;
            },
            error: () => {
                this.snackbar.show('Failed to load notification settings', 'error');
                this.loading = false;
            }
        });
    }

    toggleSetting(setting: NotificationSetting, role: 'admin' | 'patient' | 'attorney'): void {
        setting[role] = setting[role] ? 0 : 1;
    }

    saveSettings(): void {
        this.saving = true;
        const payload = {
            settings: this.settings.map(s => ({
                id: s.id,
                admin: s.admin,
                patient: s.patient,
                attorney: s.attorney
            }))
        };

        this.http.post<any>(this.apiUrl, payload).subscribe({
            next: () => {
                this.snackbar.show('Settings saved successfully', 'success');
                this.saving = false;
            },
            error: () => {
                this.snackbar.show('Failed to save settings', 'error');
                this.saving = false;
            }
        });
    }
}
